import styles from "./styles.module.scss";
import { FC, IFileProgress, Props } from "./typings";

import CloseIcon from "assets/icons/close.svg?react";
import Button from "components/Button";
import { useState, useCallback } from "react";
import { LinearProgress, TextField } from "@mui/material";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { generateSingleBinaryFromMultiplePublicKeys } from "utils/generateSingleBinaryFromMultiplePublicKeys";
import encryptContent from "utils/encryptContent";
import { handleCreate } from "utils/uploadFile";
import { readAsBase64 } from "utils/readAsBase64";
import Filedossier from "components/Filedossier/Filedossier";
import classNames from "classnames";
import Lottie from "lottie-react";
import SendAnimation from "assets/animation.json";
import ConfettiAnimation from "assets/confetti.json";
import Input from "components/Input";
import DropIcon from "assets/icons/drop.svg?react";
import { useTranslation } from "react-i18next";

const CreateContactMessageModal: FC<Props> = ({
  chatToken,
  publicKey,
  toggle,
  onSent,
  isMobile,
  defaultTitle,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(defaultTitle ?? "");
  const [body, setBody] = useState("");

  const [actualFile] = useState(0);
  const [actualProgress] = useState<number | null>(null);

  const [sending, setSending] = useState<boolean>(false);
  const [sended, setSended] = useState<boolean>(false);

  const [files, setFiles] = useState<File[]>([]);
  const [, setFilesProgress] = useState<IFileProgress[]>([]);

  const [totalSize, setTotalSize] = useState<number>(0);
  const [totalProgress, setTotalProgress] = useState<number>(0);
  const [error, setError] = useState("");

  const onDrop = useCallback((acceptedFiles: any) => {
    setFiles((old) => [...old, ...acceptedFiles]);
  }, []);

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    onDrop: onDrop,
    multiple: true,
    disabled: sending,
    noClick: true,
  });

  const handleAddFile = () => {
    open();
  };

  const send = async () => {
    try {
      const initPayload: any = {
        AttachmentsCount: files.length,
        Token: chatToken,
      };

      setSending(true);

      const response = await axios.post(
        import.meta.env.VITE_APP_API_URL + "/contactform/init",
        initPayload,
        {
          withCredentials: true,
        },
      );

      if (!response?.data?.PublicKeys) {
        setSending(false);
        return;
      }

      // token przesyłki — nagłówek X-Contact-Sid na upload/part/confirm/send
      const sid = response.data.Sid;

      const keys = await generateSingleBinaryFromMultiplePublicKeys([
        ...response.data.PublicKeys,
        publicKey,
      ]);
      const encrypted = await encryptContent(body, keys.cleanPublicKey);

      setFilesProgress(
        files.map((f) => ({
          Progress: 0,
          Size: f.size,
        })),
      );

      setTotalSize(files.reduce((sum, current) => sum + current.size, 0));

      const encryptedUri = await handleCreate(
        files,
        null,
        keys.cleanPublicKey,
        true,
        false,
        undefined,
        undefined,
        (index, progress) => {
          setFilesProgress((old) => {
            const n = [...old];
            n[index].Progress = progress;

            setTotalProgress(
              n.reduce(
                (sum, current) => sum + (current.Size * current.Progress) / 100,
                0,
              ),
            );
            return n;
          });
        },
        false,
        true,
        sid,
      );

      const uris = encryptedUri?.map((e) => ({ Uri: e.uri }));

      const sendPayload: any = {
        Attachments: uris,
        Content: await readAsBase64(encrypted),
        PrivateKey: keys.PrivateKey,
        PublicKey: keys.PublicKey,
        SenderEmail: null,
        Title: title,
        Token: chatToken,
      };

      await axios.post(
        import.meta.env.VITE_APP_API_URL + "/contactform/send",
        sendPayload,
        {
          withCredentials: true,
          headers: { "X-Contact-Sid": sid },
        },
      );

      setSending(false);
      setSended(true);
      onSent?.();

      setTimeout(() => {
        toggle?.();
      }, 1000);
    } catch (e: any) {
      console.error(e);
      setSending(false);
      setError(
        e?.response?.data?.errorCodes?.[0]?.message ??
          e?.response?.data?.title ??
          t("errors.tryAgainLater"),
      );
    }
  };

  return (
    <div
      className={classNames(
        styles.wrapper,
        isDragAccept ? styles.dropAccept : "",
        isDragReject ? styles.dropReject : "",
        isFocused ? styles.dropFocus : "",
      )}
      {...getRootProps()}
    >
      <div className={styles.title}>
        {t("chat.newMessage")}
        <CloseIcon
          className={styles.closeIcon}
          onClick={(e) => {
            toggle();
            e.stopPropagation();
          }}
        />
      </div>
      <div className={styles.content}>
        <div className={styles.sub}>
          <Input
            label={t("packages.subject")}
            placeholder={t("packages.subject")}
            value={title}
            onChange={(x) => setTitle(x)}
          ></Input>
        </div>
        <div className={styles.grow}>
          <TextField
            label={t("chat.messageContent")}
            value={body}
            onChange={(v) => setBody(v.target.value)}
            multiline
            minRows={3}
            variant="filled"
            sx={{ display: "flex", flexGrow: 1 }}
            InputProps={{
              sx: {
                height: "100%",
                alignItems: "start",
              },
            }}
          />
        </div>

        <div>
          <input {...getInputProps()} />

          {!!files.length && !isDragAccept && (
            <>
              <div className={styles.files}>
                {files.map((file: File, index: number) => (
                  <Filedossier
                    size="small"
                    key={index}
                    index={index}
                    handleRemove={(i) => {
                      setFiles((old) =>
                        old.filter((x) => old.indexOf(x) !== i),
                      );
                    }}
                    file={file}
                    actualFile={actualFile}
                    actualProgress={actualProgress}
                    newIcons={true}
                  />
                ))}
              </div>
              <div className={styles.left}>
                <div className={styles.textButton} onClick={handleAddFile}>
                  + {t("chat.addAttachment")}
                </div>
              </div>
            </>
          )}

          {!files.length && !isDragAccept && (
            <div className={classNames(styles.empty)}>
              <div className={styles.left}>
                <div className={styles.textButton} onClick={handleAddFile}>
                  {!isMobile
                    ? `+ ${t("chat.addAttachmentOrDrop")}`
                    : `+ ${t("chat.addAttachment")}`}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {!!error.length && (
        <div className={styles.error}>
          {t("errors.somethingWentWrong")}
        </div>
      )}
      <div className={styles.footer}>
        <div className={styles.info}>
          {t("chat.maxAttachmentSize")}
        </div>
        <div className={styles.bottombtn}>
        <Button text={t("common.send")} onClick={send} className={styles.sendButton} />
        </div>
      </div>

      {isDragAccept && (
        <div className={classNames(styles.overlay, styles.dragover)}>
          <div>
            <DropIcon className={styles.icon}></DropIcon>
          </div>
          <div className={styles.text}>{t("common.dropFileToAdd")}</div>
        </div>
      )}

      {sending && (
        <div className={classNames(styles.overlay, styles.sending)}>
          <Lottie
            className={classNames(styles.animation)}
            animationData={SendAnimation}
            loop={true}
          />
          {totalSize > 0 && (
            <div className={styles.progress}>
              <LinearProgress
                variant="determinate"
                value={(totalProgress / totalSize) * 100}
              />
            </div>
          )}
          <div className={styles.text}>
            {t("chat.encryptingFiles")}
          </div>
        </div>
      )}
      {sended && (
        <div className={classNames(styles.overlay, styles.sent)}>
          <Lottie
            className={classNames(styles.animation)}
            animationData={ConfettiAnimation}
            loop={true}
          />
          <div className={styles.done}>{t("chat.messageSent")}</div>
        </div>
      )}
    </div>
  );
};

export default CreateContactMessageModal;
